import { User } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { formatDateTimeLong } from '@/lib/timeFormatters'

interface AnsweredQuestionPayload {
  requestId: string
  questions: string
  answers: string
  notes: string | null
}

interface Props {
  payload: AnsweredQuestionPayload
  timestamp?: string
}

interface Question {
  id: string
  prompt: string
}
interface Answer {
  questionId: string
  value: string
}

function parseList<T>(json: string): T[] {
  try {
    const arr = JSON.parse(json)
    return Array.isArray(arr) ? (arr as T[]) : []
  } catch {
    return []
  }
}

export function AnsweredQuestionBubble({ payload, timestamp }: Props) {
  const { user } = useAuth()
  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : ''

  const questions = parseList<Question>(payload.questions)
  const answers = parseList<Answer>(payload.answers)
  const answerMap = new Map(answers.map((a) => [a.questionId, a.value]))

  const answered = questions
    .map((q) => ({ prompt: q.prompt, value: answerMap.get(q.id) }))
    .filter((qa): qa is { prompt: string; value: string } => !!qa.value)

  return (
    <div className="py-5">
      <div className="flex gap-3 justify-end items-start">
        <div className="flex flex-col items-end gap-1 max-w-[80%]">
          <div className="rounded-2xl rounded-tr-sm bg-border px-4 py-3">
            <div className="space-y-2 text-sm leading-relaxed">
              {answered.map((qa) => (
                <div key={qa.prompt}>
                  <div className="font-semibold">{qa.prompt}</div>
                  <div className="whitespace-pre-wrap">{qa.value}</div>
                </div>
              ))}
              {payload.notes && (
                <div>
                  <div className="font-semibold">Notes</div>
                  <div className="whitespace-pre-wrap">{payload.notes}</div>
                </div>
              )}
            </div>
          </div>
          {timestamp && (
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <span>{formatDateTimeLong(timestamp)}</span>
            </div>
          )}
        </div>
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold mt-0.5">
          {initials || <User className="h-3.5 w-3.5" />}
        </div>
      </div>
    </div>
  )
}
